import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Typography, Paper, Stack, Grid } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Fade from '@mui/material/Fade';
import PropTypes from 'prop-types';
import CodeBlock from '../../../utils/CodeBlock';

// Styled Components
const ContentContainer = styled.div`
  background-color: #f9f9f9;
  min-height: 100vh;
  padding: 20px;
`;

const SectionContainer = styled(Paper)`
  padding: 20px;
  margin-bottom: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

// const Header = styled(Typography)`
//   text-align: left;
//   font-size: 1.5rem;
//   font-weight: bold;
//   margin-bottom: 20px;
// `;

const SubHeader = styled(Typography)`
  font-size: 1.5rem;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const CodeBlockStyle = styled.pre`
  background: #eaeaea;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: monospace;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
  }

  th {
    background-color: #f2f2f2;
    text-align: left;
  }
`;

// Reusable Components
const Section = ({ title, children }) => (
  <SectionContainer>
    <SubHeader>{title}</SubHeader>
    {children}
  </SectionContainer>
);

// Main Component
const IPQualityAPI = () => {
  const location = useLocation();
  const openAccordionId = location.state?.openAccordionId || null; // Get the passed state
  const [expanded, setExpanded] = useState(openAccordionId);


  useEffect(() => {
    if (openAccordionId) {
      setExpanded(openAccordionId);
    } else {
      setExpanded('panel1');
    }
  }, [openAccordionId]);

  const handleExpansion = (panelId) => {
    setExpanded((prevExpanded) => (prevExpanded === panelId ? null : panelId));
  };

  const IPQualityRequestFormat = `curl --location 'https://dev-api.innowave.solutions/dev' \\\n--header 'Content-Type: application/json' \\\n--header 'x-api-key: your_api_key_here' \\\n--data '{ "ip_address": "121.2.3.1" }'`;

  const IPQualityResponseFormat = `{
    "ip": "121.2.3.1",
    "type": "ipv4",
    "continent_code": "AS",
    "continent_name": "Asia",
    "country_code": "JP",
    "country_name": "Japan",
    "region_code": "01",
    "region_name": "Hokkaido",
    "city": "Sapporo",
    "zip": "001-0906",
    "latitude": 43.1168212890625,
    "longitude": 141.31858825683594,
    "msa": "001-0906",
    "dma": "001-0906",
    "radius": "001-0906",
    "ip_routing_type": "fixed",
    "connection_type": "tx",
    "location": {
        "geoname_id": "2128295",
        "capital": "Tokyo",
        "languages": "[{\\"code\\":\\"ja\\",\\"name\\":\\"Japanese\\",\\"native\\":\\"日本語\\"}]"
    },
    "security": {
        "is_proxy": false,
        "proxy_type": null,
        "is_crawler": false,
        "crawler_name": null,
        "crawler_type": null,
        "is_tor": false,
        "threat_level": "low",
        "threat_types": null,
        "proxy_last_detected": null,
        "proxy_level": null,
        "vpn_service": null,
        "anonymizer_status": null,
        "hosting_facility": false
    }
}`;

  const IPCurencyRequestFormat = `curl --location 'https://dev-api.innowave.solutions/ip_to_currencyAndLocation' \\\n--header 'Content-Type: application/json' \\\n--header 'x-api-key: your_api_key_here' \\\n--data '{ "ip_address": "121.2.3.1" }'`;

  const IPCurencyResponseFormat = `{
    "ip": "121.2.3.1",
    "type": "ipv4",
    "continent_code": "AS",
    "continent_name": "Asia",
    "country_code": "JP",
    "country_name": "Japan",
    "region_code": "01",
    "region_name": "Hokkaido",
    "city": "Sapporo",
    "zip": "001-0026",
    "latitude": 43.0944709777832,
    "longitude": 141.3231964111328,
    "msa": "001-0026",
    "dma": "001-0026",
    "radius": "001-0026",
    "ip_routing_type": "fixed",
    "connection_type": "tx",
    "location": {
        "geoname_id": "2128295",
        "capital": "Tokyo",
        "languages": "[{"code":"ja","name":"Japanese","native":"日本語"}]"
    },
    "currency": {
        "code": "JPY",
        "name": "Japanese Yen",
        "plural": "Japanese yen",
        "symbol": "¥",
        "symbol_native": "￥"
    }
}`;

  const IPTimeLanguageRequestFormat = `curl --location 'https://dev-api.innowave.solutions/ip_to_timezoneAndLocation' \\\n--header 'Content-Type: application/json' \\\n--header 'x-api-key: your_api_key_here' \\\n--data '{ "ip_address": "121.2.3.1" }'`;

  const IPTimeLanguageResponseFormat = `{
    "ip": "121.2.3.1",
    "type": "ipv4",
    "continent_code": "AS",
    "continent_name": "Asia",
    "country_code": "JP",
    "country_name": "Japan",
    "region_code": "01",
    "region_name": "Hokkaido",
    "city": "Sapporo",
    "zip": "001-0026",
    "latitude": 43.0944709777832,
    "longitude": 141.3231964111328,
    "msa": "001-0026",
    "dma": "001-0026",
    "radius": "001-0026",
    "ip_routing_type": "fixed",
    "connection_type": "tx",
    "location": {
        "geoname_id": "2128295",
        "capital": "Tokyo",
        "languages": "[{"code":"ja","name":"Japanese","native":"日本語"}]"
    },
    "time_zone": {
        "id": "Asia/Tokyo",
        "current_time": "2025-03-28T21:44:54+09:00",
        "gmt_offset": 32400,
        "code": "JST",
        "is_daylight_saving": false
    }
}`;

  return (
    <Grid>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <h1>
          <Typography variant='title'>
            InnoWave API Hub Documentation
          </Typography>
        </h1>
      </Stack>
      {/* IP Quality Check and Location Data API */}
      <Accordion
        expanded={expanded === 'panel1'}
        onChange={() => handleExpansion('panel1')}
        slots={{ transition: Fade }}
        slotProps={{ transition: { timeout: 400 } }}
        id="panel1"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
          <Typography variant='h5'>IP Quality Check and Location Data API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ContentContainer>
            <Section title="Overview">
              <Typography>
                IP Quality Check and Location Data API identifies IP reputation, detects fraud, and provides precise
                geographic location insights.
              </Typography>
            </Section>

            <Section title="Base URL">
              <Typography>
                <strong>Dev:</strong> https://dev-api.innowave.solutions/ipquality
                <br />
                <strong>Prod:</strong> https://api.innowave.solutions/ipquality
              </Typography>
            </Section>

            <Section title="Authentication">
              <Typography>All requests must include an API key in the header.</Typography>
              <Typography variant="h6">Header Parameters</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Content-Type</td>
                    <td>Yes</td>
                    <td>Must be application/json</td>
                  </tr>
                  <tr>
                    <td>x-api-key</td>
                    <td>Yes</td>
                    <td>Your API key</td>
                  </tr>
                </tbody>
              </Table>
            </Section>

            <Section title="Endpoint">
              <Typography variant="h6">Check IP Quality and Location</Typography>
              <Typography variant="h6">Request Format</Typography>
              <CodeBlockStyle>
                <CodeBlock>{IPQualityRequestFormat}</CodeBlock>
              </CodeBlockStyle>
              <Typography variant="h6">Response Format</Typography>
              <CodeBlockStyle>
                <CodeBlock>{IPQualityResponseFormat}</CodeBlock>
              </CodeBlockStyle>
            </Section>

            <Section title="Response Fields Description">
              <Typography variant="h6">Root Level Fields</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>ip</td>
                    <td>string</td>
                    <td>The queried IP address</td>
                    <td>"121.2.3.1"</td>
                  </tr>
                  <tr>
                    <td>type</td>
                    <td>string</td>
                    <td>IP version type</td>
                    <td>"ipv4"</td>
                  </tr>
                  <tr>
                    <td>continent_code</td>
                    <td>string</td>
                    <td>Two-letter continent code</td>
                    <td>"AS"</td>
                  </tr>
                  <tr>
                    <td>continent_name</td>
                    <td>string</td>
                    <td>Full continent name</td>
                    <td>"Asia"</td>
                  </tr>
                  <tr>
                    <td>country_code</td>
                    <td>string</td>
                    <td>Two-letter country code</td>
                    <td>"JP"</td>
                  </tr>
                  <tr>
                    <td>country_name</td>
                    <td>string</td>
                    <td>Full country name</td>
                    <td>"Japan"</td>
                  </tr>
                  <tr>
                    <td>region_code</td>
                    <td>string</td>
                    <td>Region/state code</td>
                    <td>"01"</td>
                  </tr>
                  <tr>
                    <td>region_name</td>
                    <td>string</td>
                    <td>Full region/state name</td>
                    <td>"Hokkaido"</td>
                  </tr>
                  <tr>
                    <td>city</td>
                    <td>string</td>
                    <td>City name</td>
                    <td>"Sapporo"</td>
                  </tr>
                  <tr>
                    <td>zip</td>
                    <td>string</td>
                    <td>Postal/ZIP code</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>latitude</td>
                    <td>number</td>
                    <td>Geographic latitude</td>
                    <td>43.1168212890625</td>
                  </tr>
                  <tr>
                    <td>longitude</td>
                    <td>number</td>
                    <td>Geographic longitude</td>
                    <td>141.31858825683594</td>
                  </tr>
                  <tr>
                    <td>msa</td>
                    <td>string</td>
                    <td>Metropolitan Statistical Area</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>dma</td>
                    <td>string</td>
                    <td>Designated Market Area code</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>radius</td>
                    <td>string</td>
                    <td>Radius of the geographic area</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>ip_routing_type</td>
                    <td>string</td>
                    <td>Type of IP routing</td>
                    <td>"fixed"</td>
                  </tr>
                  <tr>
                    <td>connection_type</td>
                    <td>string</td>
                    <td>Type of network connection</td>
                    <td>"tx"</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>Location Object</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>geoname_id</td>
                    <td>string</td>
                    <td>Geonames unique ID for the location</td>
                    <td>"2128295"</td>
                  </tr>
                  <tr>
                    <td>capital</td>
                    <td>string</td>
                    <td>Capital city of the country</td>
                    <td>"Tokyo"</td>
                  </tr>
                  <tr>
                    <td>languages</td>
                    <td>array</td>
                    <td>List of languages spoken in the country</td>
                    <td>{JSON.stringify([{ code: "ja", name: "Japanese", native: "日本語" }])}</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>Security Object</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>is_proxy</td>
                    <td>boolean</td>
                    <td>Whether IP is a proxy</td>
                    <td>false</td>
                  </tr>
                  <tr>
                    <td>proxy_type</td>
                    <td>string</td>
                    <td>Type of proxy if detected</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>is_crawler</td>
                    <td>boolean</td>
                    <td>Whether IP is a crawler</td>
                    <td>false</td>
                  </tr>
                  <tr>
                    <td>crawler_name</td>
                    <td>string</td>
                    <td>Name of crawler if detected</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>crawler_type</td>
                    <td>string</td>
                    <td>Type of crawler if detected</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>is_tor</td>
                    <td>boolean</td>
                    <td>Whether IP is a Tor exit node</td>
                    <td>false</td>
                  </tr>
                  <tr>
                    <td>threat_level</td>
                    <td>string</td>
                    <td>Assessment of threat level</td>
                    <td>"low"</td>
                  </tr>
                  <tr>
                    <td>threat_types</td>
                    <td>string</td>
                    <td>Types of threats if detected</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>proxy_last_detected</td>
                    <td>string</td>
                    <td>Last proxy detection date</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>proxy_level</td>
                    <td>string</td>
                    <td>Proxy risk level</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>vpn_service</td>
                    <td>string</td>
                    <td>VPN service name if detected</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>anonymizer_status</td>
                    <td>string</td>
                    <td>Anonymization status</td>
                    <td>null</td>
                  </tr>
                  <tr>
                    <td>hosting_facility</td>
                    <td>boolean</td>
                    <td>Whether IP is from hosting facility</td>
                    <td>false</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>HTTP Response Codes</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Status Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>200</td>
                    <td>Successful request</td>
                  </tr>
                  <tr>
                    <td>400</td>
                    <td>Bad request (invalid IP address)</td>
                  </tr>
                  <tr>
                    <td>403</td>
                    <td>Forbidden (invalid API key)</td>
                  </tr>
                  <tr>
                    <td>429</td>
                    <td>Too many requests</td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td>Internal server error</td>
                  </tr>

                </tbody>
              </Table>

            </Section>
          </ContentContainer>
        </AccordionDetails>
      </Accordion>

      {/* IP to Currency and Location Data API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2-header">
          <Typography variant='h5'> IP to Currency and Location Data API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ContentContainer>
            <Section title="Overview">
              <Typography>Retrieve the currency associated with a given IP address by querying this API. Simply input the IP address, and the API will return the corresponding currency information.</Typography>
            </Section>

            <Section title="Base URL">
              <Typography>
                <strong>Dev:</strong> https://dev-api.innowave.solutions/ip_to_currencyAndLocation
                <br />
                <strong>Prod:</strong> https://api.innowave.solutions/ip_to_currencyAndLocation
              </Typography>
            </Section>

            <Section title="Authentication">
              <Typography>All requests must include an API key in the header.</Typography>
              <Typography variant="h6">Header Parameters</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Content-Type</td>
                    <td>Yes</td>
                    <td>Must be application/json</td>
                  </tr>
                  <tr>
                    <td>x-api-key</td>
                    <td>Yes</td>
                    <td>Your API key</td>
                  </tr>
                </tbody>
              </Table>
            </Section>

            <Section title="Endpoint">
              <Typography variant="h6">Check IP Quality and Location</Typography>
              <Typography variant="h6">Request Format</Typography>
              <CodeBlockStyle>
                <CodeBlock>{IPCurencyRequestFormat}</CodeBlock>
              </CodeBlockStyle>
              <Typography variant="h6">Response Format</Typography>
              <CodeBlockStyle>
                <CodeBlock>{IPCurencyResponseFormat}</CodeBlock>
              </CodeBlockStyle>
            </Section>

            <Section title="Response Fields Description">
              <Typography variant="h6">Root Level Fields</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>ip</td>
                    <td>string</td>
                    <td>The queried IP address</td>
                    <td>"121.2.3.1"</td>
                  </tr>
                  <tr>
                    <td>type</td>
                    <td>string</td>
                    <td>IP version type</td>
                    <td>"ipv4"</td>
                  </tr>
                  <tr>
                    <td>continent_code</td>
                    <td>string</td>
                    <td>Two-letter continent code</td>
                    <td>"AS"</td>
                  </tr>
                  <tr>
                    <td>continent_name</td>
                    <td>string</td>
                    <td>Full continent name</td>
                    <td>"Asia"</td>
                  </tr>
                  <tr>
                    <td>country_code</td>
                    <td>string</td>
                    <td>Two-letter country code</td>
                    <td>"JP"</td>
                  </tr>
                  <tr>
                    <td>country_name</td>
                    <td>string</td>
                    <td>Full country name</td>
                    <td>"Japan"</td>
                  </tr>
                  <tr>
                    <td>region_code</td>
                    <td>string</td>
                    <td>Region/state code</td>
                    <td>"01"</td>
                  </tr>
                  <tr>
                    <td>region_name</td>
                    <td>string</td>
                    <td>Full region/state name</td>
                    <td>"Hokkaido"</td>
                  </tr>
                  <tr>
                    <td>city</td>
                    <td>string</td>
                    <td>City name</td>
                    <td>"Sapporo"</td>
                  </tr>
                  <tr>
                    <td>zip</td>
                    <td>string</td>
                    <td>Postal/ZIP code</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>latitude</td>
                    <td>number</td>
                    <td>Geographic latitude</td>
                    <td>43.1168212890625</td>
                  </tr>
                  <tr>
                    <td>longitude</td>
                    <td>number</td>
                    <td>Geographic longitude</td>
                    <td>141.31858825683594</td>
                  </tr>
                  <tr>
                    <td>msa</td>
                    <td>string</td>
                    <td>Metropolitan Statistical Area</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>dma</td>
                    <td>string</td>
                    <td>Designated Market Area code</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>radius</td>
                    <td>string</td>
                    <td>Radius of the geographic area</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>ip_routing_type</td>
                    <td>string</td>
                    <td>Type of IP routing</td>
                    <td>"fixed"</td>
                  </tr>
                  <tr>
                    <td>connection_type</td>
                    <td>string</td>
                    <td>Type of network connection</td>
                    <td>"tx"</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>Location Object</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>geoname_id</td>
                    <td>string</td>
                    <td>Geonames unique ID for the location</td>
                    <td>"2128295"</td>
                  </tr>
                  <tr>
                    <td>capital</td>
                    <td>string</td>
                    <td>Capital city of the country</td>
                    <td>"Tokyo"</td>
                  </tr>
                  <tr>
                    <td>languages</td>
                    <td>array</td>
                    <td>List of languages spoken in the country</td>
                    <td>{JSON.stringify([{ code: "ja", name: "Japanese", native: "日本語" }])}</td>
                  </tr>
                </tbody>
              </Table>


              <Typography variant="h6" mt={3}>Currency Object</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>code</td>
                    <td>string</td>
                    <td>Three-letter currency code</td>
                    <td>"JPY"</td>
                  </tr>
                  <tr>
                    <td>name</td>
                    <td>string</td>
                    <td>Full currency name</td>
                    <td>"Japanese Yen"</td>
                  </tr>
                  <tr>
                    <td>plural</td>
                    <td>string</td>
                    <td>Plural form of the currency name</td>
                    <td>"Japanese yen"</td>
                  </tr>
                  <tr>
                    <td>symbol</td>
                    <td>string</td>
                    <td>Standard currency symbol</td>
                    <td>"¥"</td>
                  </tr>
                  <tr>
                    <td>symbol_native</td>
                    <td>string</td>
                    <td>Native currency symbol</td>
                    <td>"￥"</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>HTTP Response Codes</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Status Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>200</td>
                    <td>Successful request</td>
                  </tr>
                  <tr>
                    <td>400</td>
                    <td>Bad request (invalid IP address)</td>
                  </tr>
                  <tr>
                    <td>403</td>
                    <td>Forbidden (invalid API key)</td>
                  </tr>
                  <tr>
                    <td>429</td>
                    <td>Too many requests</td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td>Internal server error</td>
                  </tr>

                </tbody>
              </Table>

            </Section>
          </ContentContainer>
        </AccordionDetails>
      </Accordion>

      {/* IP to Time-Zone and Location Data API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2-header">
          <Typography variant='h5'>IP to Time-Zone and Location Data API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ContentContainer>
            <Section title="Overview">
              <Typography>Retrieve the time zone and language associated with a given IP address using this API. Simply input the IP address, and the API will return the corresponding time zone and language details.</Typography>
            </Section>

            <Section title="Base URL">
              <Typography>
                <strong>Dev:</strong> https://dev-api.innowave.solutions/ip_to_timezoneAndLocation
                <br />
                <strong>Prod:</strong> https://api.innowave.solutions/ip_to_timezoneAndLocation
              </Typography>
            </Section>

            <Section title="Authentication">
              <Typography>All requests must include an API key in the header.</Typography>
              <Typography variant="h6">Header Parameters</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Content-Type</td>
                    <td>Yes</td>
                    <td>Must be application/json</td>
                  </tr>
                  <tr>
                    <td>x-api-key</td>
                    <td>Yes</td>
                    <td>Your API key</td>
                  </tr>
                </tbody>
              </Table>
            </Section>

            <Section title="Endpoint">
              <Typography variant="h6">Check IP Quality and Location</Typography>
              <Typography variant="h6">Request Format</Typography>
              <CodeBlockStyle>
                <CodeBlock>{IPTimeLanguageRequestFormat}</CodeBlock>
              </CodeBlockStyle>
              <Typography variant="h6">Response Format</Typography>
              <CodeBlockStyle>
                <CodeBlock>{IPTimeLanguageResponseFormat}</CodeBlock>
              </CodeBlockStyle>
            </Section>

            <Section title="Response Fields Description">
              <Typography variant="h6">Root Level Fields</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>ip</td>
                    <td>string</td>
                    <td>The queried IP address</td>
                    <td>"121.2.3.1"</td>
                  </tr>
                  <tr>
                    <td>type</td>
                    <td>string</td>
                    <td>IP version type</td>
                    <td>"ipv4"</td>
                  </tr>
                  <tr>
                    <td>continent_code</td>
                    <td>string</td>
                    <td>Two-letter continent code</td>
                    <td>"AS"</td>
                  </tr>
                  <tr>
                    <td>continent_name</td>
                    <td>string</td>
                    <td>Full continent name</td>
                    <td>"Asia"</td>
                  </tr>
                  <tr>
                    <td>country_code</td>
                    <td>string</td>
                    <td>Two-letter country code</td>
                    <td>"JP"</td>
                  </tr>
                  <tr>
                    <td>country_name</td>
                    <td>string</td>
                    <td>Full country name</td>
                    <td>"Japan"</td>
                  </tr>
                  <tr>
                    <td>region_code</td>
                    <td>string</td>
                    <td>Region/state code</td>
                    <td>"01"</td>
                  </tr>
                  <tr>
                    <td>region_name</td>
                    <td>string</td>
                    <td>Full region/state name</td>
                    <td>"Hokkaido"</td>
                  </tr>
                  <tr>
                    <td>city</td>
                    <td>string</td>
                    <td>City name</td>
                    <td>"Sapporo"</td>
                  </tr>
                  <tr>
                    <td>zip</td>
                    <td>string</td>
                    <td>Postal/ZIP code</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>latitude</td>
                    <td>number</td>
                    <td>Geographic latitude</td>
                    <td>43.1168212890625</td>
                  </tr>
                  <tr>
                    <td>longitude</td>
                    <td>number</td>
                    <td>Geographic longitude</td>
                    <td>141.31858825683594</td>
                  </tr>
                  <tr>
                    <td>msa</td>
                    <td>string</td>
                    <td>Metropolitan Statistical Area</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>dma</td>
                    <td>string</td>
                    <td>Designated Market Area code</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>radius</td>
                    <td>string</td>
                    <td>Radius of the geographic area</td>
                    <td>"001-0906"</td>
                  </tr>
                  <tr>
                    <td>ip_routing_type</td>
                    <td>string</td>
                    <td>Type of IP routing</td>
                    <td>"fixed"</td>
                  </tr>
                  <tr>
                    <td>connection_type</td>
                    <td>string</td>
                    <td>Type of network connection</td>
                    <td>"tx"</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>Location Object</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>geoname_id</td>
                    <td>string</td>
                    <td>Geonames unique ID for the location</td>
                    <td>"2128295"</td>
                  </tr>
                  <tr>
                    <td>capital</td>
                    <td>string</td>
                    <td>Capital city of the country</td>
                    <td>"Tokyo"</td>
                  </tr>
                  <tr>
                    <td>languages</td>
                    <td>array</td>
                    <td>List of languages spoken in the country</td>
                    <td>{JSON.stringify([{ code: "ja", name: "Japanese", native: "日本語" }])}</td>
                  </tr>
                </tbody>
              </Table>


              <Typography variant="h6" mt={3}>Time_Zone Object</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>id</td>
                    <td>string</td>
                    <td>Time zone identifier</td>
                    <td>"Asia/Tokyo"</td>
                  </tr>
                  <tr>
                    <td>current_time</td>
                    <td>string</td>
                    <td>Current date and time in the given time zone</td>
                    <td>"2025-03-28T21:44:54+09:00"</td>
                  </tr>
                  <tr>
                    <td>gmt_offset</td>
                    <td>number</td>
                    <td>Time difference from GMT in seconds</td>
                    <td>32400</td>
                  </tr>
                  <tr>
                    <td>code</td>
                    <td>string</td>
                    <td>Abbreviation of the time zone</td>
                    <td>"JST"</td>
                  </tr>
                  <tr>
                    <td>is_daylight_saving</td>
                    <td>boolean</td>
                    <td>Indicates if daylight saving time is active</td>
                    <td>false</td>
                  </tr>
                </tbody>
              </Table>

              <Typography variant="h6" mt={3}>HTTP Response Codes</Typography>
              <Table>
                <thead>
                  <tr>
                    <th>Status Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>200</td>
                    <td>Successful request</td>
                  </tr>
                  <tr>
                    <td>400</td>
                    <td>Bad request (invalid IP address)</td>
                  </tr>
                  <tr>
                    <td>403</td>
                    <td>Forbidden (invalid API key)</td>
                  </tr>
                  <tr>
                    <td>429</td>
                    <td>Too many requests</td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td>Internal server error</td>
                  </tr>

                </tbody>
              </Table>
            </Section>
          </ContentContainer>
        </AccordionDetails>
      </Accordion>

      {/* Mobile Number Verification API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2-header">
          <Typography variant='h5'>Mobile Number Verification API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography >Verify phone numbers in real time to enhance security, reduce fake sign-ups, and improve user
            authentication.</Typography>
        </AccordionDetails>
      </Accordion>

      {/* Geocoding &amp; Reverse Geocoding API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2-header">
          <Typography variant='h5'> Geocoding &amp; Reverse Geocoding API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>Convert addresses into geographic coordinates and vice versa for accurate location mapping
            and enhanced user experiences.</Typography>
        </AccordionDetails>
      </Accordion>

      {/* Name Matching API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2-header">
          <Typography variant='h5'>Name Matching API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>Ensure accurate identity verification with intelligent name-matching algorithms, reducing errors
            in customer records and fraud detection.</Typography>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

export default IPQualityAPI;


